import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Alert,
  Chip,
  Divider,
  Grid,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Link,
  alpha,
  useTheme,
  TextField,
  IconButton,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Tooltip,
  Popover,
  ClickAwayListener,
  styled
} from '@mui/material';
import {
  Inventory as InventoryIcon,
  Refresh as RefreshIcon,
  ViewList as ViewListIcon,
  Launch as LaunchIcon,
  Money as MoneyIcon,
  Assignment as AssignmentIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Edit as EditIcon,
  AddCircle as AddCircleIcon
} from '@mui/icons-material';
import { projectAPI } from '../../apis';
import { Material, MaterialMetadata } from '../../types';

interface ProjectMaterialsProps {
  projectId: string;
}

const defaultMaterial = (): Material => ({
  id: Date.now(), // Temporary ID for new materials
  name: '',
  quantity: 0,
  dimensions: '',
  unit_price: 0,
  net_price: 0,
  notes: 'General'
});

// Add a styled component for Excel-like cells
const ExcelCell = styled(Box)(({ theme }) => ({
  cursor: 'pointer',
  border: '1px solid #e0e0e0',
  borderRadius: '0px',
  padding: '6px 8px',
  minHeight: '32px',
  transition: 'all 0.1s ease-in-out',
  display: 'flex',
  alignItems: 'center',
  backgroundColor: '#ffffff',
  '&:hover': {
    backgroundColor: '#f1f8ff',
    borderColor: '#bbdefb',
  },
  '&:focus': {
    outline: 'none',
    borderColor: theme.palette.primary.main,
    boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.25)}`,
  }
}));

const ProjectMaterials: React.FC<ProjectMaterialsProps> = ({ projectId }) => {
  const theme = useTheme();
  const [materials, setMaterials] = useState<Material[]>([]);
  const [sources, setSources] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<string | null>(null);
  const [metadataId, setMetadataId] = useState<number | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [isAddingSectionOpen, setIsAddingSectionOpen] = useState(false);
  const [newSectionName, setNewSectionName] = useState('');
  
  // Replace dialog state with popover state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [currentEditMaterial, setCurrentEditMaterial] = useState<Material | null>(null);
  const [currentEditField, setCurrentEditField] = useState<keyof Material | null>(null);
  const [editFieldValue, setEditFieldValue] = useState<string>('');
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const fetchMaterials = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await projectAPI.getProjectMaterials(projectId);
      
      if (response.status === 'success' && response.data) {
        setMaterials(response.data.materials || []);
        setSources(response.data.sources || []);
        setLastUpdated(response.data.last_updated || null);
        setMetadataId(response.data.metadata_id || null);
      } else {
        setError('No materials found for this project.');
      }
    } catch (error) {
      console.error('Error fetching materials:', error);
      setError('Failed to fetch materials. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const analyzeProjectMaterials = async () => {
    try {
      setAnalyzing(true);
      setError(null);
      
      const response = await projectAPI.analyzeProjectMaterials(projectId);
      
      if (response.status === 'success') {
        setMaterials(response.data.materials || []);
        setSources(response.data.sources || []);
        setLastUpdated(response.data.last_updated || null);
        setMetadataId(response.data.metadata_id || null);
        
        // Display the message from API if provided
        if (response.message) {
          setError(response.message);
        } else if (response.data.materials?.length === 0) {
          setError('No materials were found in the documents.');
        }
      } else {
        setError(response.message || 'Failed to analyze materials.');
      }
    } catch (error) {
      console.error('Error analyzing materials:', error);
      setError('Failed to analyze materials. Please try again.');
    } finally {
      setAnalyzing(false);
    }
  };

  const saveMaterials = async () => {
    try {
      setSaving(true);
      setError(null);

      // Call backend API to save materials
      // For now, we'll simulate this by sending a POST request to the materials endpoint
      const response = await fetch(`/projects/${projectId}/materials`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          materials,
          metadata_id: metadataId
        })
      });

      const data = await response.json();
      
      if (data.status === 'success') {
        setError('Materials saved successfully');
        setLastUpdated(new Date().toISOString());
      } else {
        setError(data.message || 'Failed to save materials.');
      }
    } catch (error) {
      console.error('Error saving materials:', error);
      setError('Failed to save materials. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (projectId) {
      fetchMaterials();
    }
  }, [projectId]);

  const getTotalCost = () => {
    return materials.reduce((sum, material) => sum + (material.net_price || 0), 0);
  };

  // Group materials by notes (section/room/floor)
  const groupMaterialsBySection = (materials: Material[]) => {
    const groups: { [section: string]: Material[] } = {};
    materials.forEach((material) => {
      const section = material.notes && material.notes.trim() ? material.notes.trim() : 'Unspecified Section';
      if (!groups[section]) groups[section] = [];
      groups[section].push(material);
    });
    return groups;
  };

  const handleMaterialChange = (materialId: number, field: keyof Material, value: any) => {
    setMaterials(prevMaterials => 
      prevMaterials.map(material => {
        if (material.id === materialId) {
          // Update the specified field
          const updatedMaterial = { ...material, [field]: value };
          
          // If quantity or unit_price changes, recalculate net_price
          if (field === 'quantity' || field === 'unit_price') {
            const quantity = field === 'quantity' ? parseFloat(value) || 0 : material.quantity || 0;
            const unitPrice = field === 'unit_price' ? parseFloat(value) || 0 : material.unit_price || 0;
            updatedMaterial.net_price = quantity * unitPrice;
          }
          
          return updatedMaterial;
        }
        return material;
      })
    );
  };

  const addMaterial = (sectionName: string) => {
    const newMaterial = defaultMaterial();
    newMaterial.notes = sectionName;
    setMaterials([...materials, newMaterial]);
  };

  const deleteMaterial = (materialId: number) => {
    setMaterials(materials.filter(material => material.id !== materialId));
  };

  const addNewSection = () => {
    if (newSectionName.trim()) {
      const newMaterial = defaultMaterial();
      newMaterial.notes = newSectionName;
      setMaterials([...materials, newMaterial]);
      setNewSectionName('');
      setIsAddingSectionOpen(false);
    }
  };

  const deleteSection = (sectionName: string) => {
    if (window.confirm(`Are you sure you want to delete all materials in the "${sectionName}" section?`)) {
      setMaterials(materials.filter(material => material.notes !== sectionName));
    }
  };

  const handleAddSectionOpen = () => {
    setIsAddingSectionOpen(true);
  };

  const handleAddSectionClose = () => {
    setIsAddingSectionOpen(false);
    setNewSectionName('');
  };

  const toggleEditMode = () => {
    setEditMode(!editMode);
  };

  const handleOpenEditDialog = (material: Material, field: keyof Material, event: React.MouseEvent<HTMLElement>) => {
    // Set anchor element to the clicked element for proper positioning
    setAnchorEl(event.currentTarget);
    setCurrentEditMaterial(material);
    setCurrentEditField(field);
    setEditFieldValue(material[field]?.toString() || '');
    setEditDialogOpen(true);
  };

  const handleCloseEditDialog = (save = false) => {
    if (save && currentEditMaterial && currentEditField) {
      handleMaterialChange(currentEditMaterial.id, currentEditField, 
        currentEditField === 'quantity' || currentEditField === 'unit_price' 
          ? parseFloat(editFieldValue) 
          : editFieldValue
      );
    }
    
    setEditDialogOpen(false);
    setAnchorEl(null);
    // Don't reset these immediately to avoid UI flicker during animation
    setTimeout(() => {
      setCurrentEditMaterial(null);
      setCurrentEditField(null);
    }, 200);
  };

  const handleSaveEditDialog = () => {
    handleCloseEditDialog(true);
  };

  const handleClickAway = () => {
    handleSaveEditDialog(); // Save changes when clicking away
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSaveEditDialog();
    } else if (e.key === 'Escape') {
      handleCloseEditDialog(false); // Don't save on Escape
    }
  };

  const groupedMaterials = groupMaterialsBySection(materials);

  const renderEditableTable = (section: string, sectionMaterials: Material[]) => (
    <TableContainer component={Paper} sx={{ mb: 2, border: '1px solid #e0e0e0', borderRadius: '2px' }}>
      <Table size="small" sx={{ '& .MuiTableCell-root': { borderColor: '#e0e0e0', padding: '6px 8px' } }}>
        <TableHead>
          <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
            <TableCell width="40%" sx={{ fontWeight: 600 }}>Material</TableCell>
            <TableCell align="right" width="15%" sx={{ fontWeight: 600 }}>Quantity</TableCell>
            <TableCell align="right" width="15%" sx={{ fontWeight: 600 }}>Dimensions</TableCell>
            <TableCell align="right" width="15%" sx={{ fontWeight: 600 }}>Unit Price</TableCell>
            <TableCell align="right" width="15%" sx={{ fontWeight: 600 }}>Net Price</TableCell>
            {editMode && <TableCell align="center" width="10%" sx={{ fontWeight: 600 }}>Actions</TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {sectionMaterials.map((material) => (
            <TableRow key={material.id} hover sx={{ '&:hover': { backgroundColor: '#fafafa' } }}>
              <TableCell width="40%" sx={{ p: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, width: '100%' }}>
                  <InventoryIcon sx={{ color: 'primary.main', fontSize: 18, flexShrink: 0, mt: 0.5 }} />
                  {editMode ? (
                    <ExcelCell 
                      onClick={(event) => handleOpenEditDialog(material, 'name', event)}
                      sx={{ width: '100%', justifyContent: 'flex-start' }}
                    >
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          wordBreak: 'break-word',
                          whiteSpace: 'normal',
                          overflow: 'visible',
                          textOverflow: 'initial'
                        }}
                      >
                        {material.name || 'Click to add name...'}
                      </Typography>
                    </ExcelCell>
                  ) : (
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        wordBreak: 'break-word', 
                        whiteSpace: 'normal',
                        width: '100%'
                      }}
                    >
                      {material.name}
                    </Typography>
                  )}
                </Box>
              </TableCell>
              <TableCell align="right" width="15%" sx={{ p: 1 }}>
                {editMode ? (
                  <ExcelCell 
                    onClick={(event) => handleOpenEditDialog(material, 'quantity', event)}
                    sx={{ justifyContent: 'flex-end' }}
                  >
                    {material.quantity || '0'}
                  </ExcelCell>
                ) : (
                  material.quantity
                )}
              </TableCell>
              <TableCell align="right" width="15%" sx={{ p: 1 }}>
                {editMode ? (
                  <ExcelCell 
                    onClick={(event) => handleOpenEditDialog(material, 'dimensions', event)}
                    sx={{ justifyContent: 'flex-end' }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        wordBreak: 'break-word',
                        whiteSpace: 'normal',
                        overflow: 'visible',
                        textOverflow: 'initial'
                      }}
                    >
                      {material.dimensions || 'Click to add dimensions...'}
                    </Typography>
                  </ExcelCell>
                ) : (
                  material.dimensions || '-'
                )}
              </TableCell>
              <TableCell align="right" width="15%" sx={{ p: 1 }}>
                {editMode ? (
                  <ExcelCell 
                    onClick={(event) => handleOpenEditDialog(material, 'unit_price', event)}
                    sx={{ justifyContent: 'flex-end' }}
                  >
                    ${material.unit_price?.toFixed(2) || '0.00'}
                  </ExcelCell>
                ) : (
                  material.unit_price ? `$${material.unit_price.toFixed(2)}` : '-'
                )}
              </TableCell>
              <TableCell align="right" width="15%" sx={{ p: 1, backgroundColor: editMode ? '#f9f9f9' : 'transparent' }}>
                {material.net_price ? `$${material.net_price.toFixed(2)}` : '-'}
              </TableCell>
              {editMode && (
                <TableCell align="center" width="10%" sx={{ p: 1 }}>
                  <IconButton 
                    color="error" 
                    onClick={() => deleteMaterial(material.id)}
                    size="small"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              )}
            </TableRow>
          ))}
          {/* Subtotal row for this section */}
          <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
            <TableCell colSpan={3} />
            <TableCell align="right" sx={{ fontWeight: 700 }}>
              Subtotal:
            </TableCell>
            <TableCell align="right" sx={{ fontWeight: 700 }}>
              ${sectionMaterials.reduce((sum, m) => sum + (m.net_price || 0), 0).toFixed(2)}
            </TableCell>
            {editMode && <TableCell />}
          </TableRow>
          {/* Add material row button (only in edit mode) */}
          {editMode && (
            <TableRow>
              <TableCell colSpan={editMode ? 6 : 5} align="center" sx={{ border: 'none', pt: 1, pb: 0 }}>
                <Button
                  startIcon={<AddIcon />}
                  onClick={() => addMaterial(section)}
                  size="small"
                  variant="outlined"
                  sx={{ borderRadius: '2px' }}
                >
                  Add Row
                </Button>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  // Add Section Dialog
  const addSectionDialog = (
    <Dialog open={isAddingSectionOpen} onClose={handleAddSectionClose}>
      <DialogTitle>Add New Section</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Enter a name for the new section (room, area, etc.):
        </DialogContentText>
        <TextField
          autoFocus
          margin="dense"
          label="Section Name"
          fullWidth
          variant="outlined"
          value={newSectionName}
          onChange={(e) => setNewSectionName(e.target.value)}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleAddSectionClose}>Cancel</Button>
        <Button onClick={addNewSection} color="primary">Add</Button>
      </DialogActions>
    </Dialog>
  );

  // Replace with updated popover that includes ClickAwayListener
  const materialEditPopover = (
    <Popover
      open={editDialogOpen}
      anchorEl={anchorEl}
      onClose={(event, reason) => {
        // Save when closing the popover, unless it's with the Escape key
        if (reason !== 'escapeKeyDown') {
          handleSaveEditDialog();
        } else {
          handleCloseEditDialog(false);
        }
      }}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      PaperProps={{
        sx: {
          p: 0,
          width: 'auto',
          minWidth: currentEditField === 'name' || currentEditField === 'dimensions' ? 320 : 180,
          boxShadow: '0px 2px 6px rgba(0,0,0,0.25)',
          border: `2px solid ${theme.palette.primary.main}`,
          borderRadius: 0,
          overflow: 'visible'
        }
      }}
      disableRestoreFocus
    >
      <ClickAwayListener onClickAway={handleClickAway}>
        <Box>
          {currentEditField === 'name' || currentEditField === 'dimensions' ? (
            <TextField
              autoFocus
              fullWidth
              multiline
              rows={3}
              variant="outlined"
              size="small"
              value={editFieldValue}
              onChange={(e) => setEditFieldValue(e.target.value)}
              onKeyDown={handleKeyDown}
              sx={{ 
                '& .MuiOutlinedInput-root': {
                  borderRadius: 0,
                  bgcolor: 'white',
                  '& fieldset': {
                    border: 'none'
                  },
                  '&:hover fieldset': {
                    border: 'none'
                  },
                  '&.Mui-focused fieldset': {
                    border: 'none'
                  }
                },
                '& .MuiInputBase-input': {
                  padding: '8px 10px',
                }
              }}
            />
          ) : (
            <TextField
              autoFocus
              fullWidth
              type="number"
              inputProps={{ 
                step: 0.01,
                min: 0
              }}
              variant="outlined"
              size="small"
              value={editFieldValue}
              onChange={(e) => setEditFieldValue(e.target.value)}
              onKeyDown={handleKeyDown}
              InputProps={currentEditField === 'unit_price' ? {
                startAdornment: <Typography variant="caption">$</Typography>
              } : undefined}
              sx={{ 
                '& .MuiOutlinedInput-root': {
                  borderRadius: 0,
                  bgcolor: 'white',
                  '& fieldset': {
                    border: 'none'
                  },
                  '&:hover fieldset': {
                    border: 'none'
                  },
                  '&.Mui-focused fieldset': {
                    border: 'none'
                  }
                },
                '& .MuiInputBase-input': {
                  padding: '8px 10px',
                  textAlign: 'right'
                }
              }}
            />
          )}
          
          {/* Excel-like bottom corner indicator - small visual cue */}
          <Box 
            sx={{
              position: 'absolute',
              right: -6,
              bottom: -6,
              width: 8,
              height: 8,
              bgcolor: theme.palette.primary.main,
              borderRadius: '50%',
              border: '1px solid white'
            }}
          />
        </Box>
      </ClickAwayListener>
    </Popover>
  );

  return (
    <Grid container spacing={3}>
      {/* Materials Table */}
      <Grid item xs={12} lg={8}>
        <Card sx={{ borderRadius: '16px' }}>
          <CardHeader
            title="Project Materials"
            action={
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Tooltip title={editMode ? "Exit Edit Mode" : "Edit Materials"}>
                  <Button
                    variant="outlined"
                    color={editMode ? "secondary" : "primary"}
                    startIcon={<EditIcon />}
                    onClick={toggleEditMode}
                    sx={{
                      borderRadius: '8px',
                      textTransform: 'none',
                      fontWeight: 500
                    }}
                  >
                    {editMode ? 'Exit Edit' : 'Edit'}
                  </Button>
                </Tooltip>
                
                {editMode && (
                  <Tooltip title="Save Changes">
                    <Button
                      variant="contained"
                      color="success"
                      startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                      onClick={saveMaterials}
                      disabled={saving}
                      sx={{
                        borderRadius: '8px',
                        textTransform: 'none',
                        fontWeight: 500
                      }}
                    >
                      {saving ? 'Saving...' : 'Save'}
                    </Button>
                  </Tooltip>
                )}
                
                {!editMode && (
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={analyzing ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />}
                    onClick={analyzeProjectMaterials}
                    disabled={analyzing}
                    sx={{
                      borderRadius: '8px',
                      textTransform: 'none',
                      fontWeight: 500
                    }}
                  >
                    {analyzing ? 'Analyzing...' : 'Analyze Materials'}
                  </Button>
                )}
              </Box>
            }
            sx={{
              p: 3,
              pb: 0,
              '& .MuiCardHeader-title': {
                fontSize: '1.25rem',
                fontWeight: 600,
              },
            }}
          />
          <CardContent sx={{ p: 3 }}>
            {error && (
              <Alert
                severity="info"
                sx={{
                  mb: 2,
                  borderRadius: '8px'
                }}
                onClose={() => setError(null)}
              >
                {error}
              </Alert>
            )}

            {editMode && (
              <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  startIcon={<AddCircleIcon />}
                  variant="outlined"
                  color="primary"
                  onClick={handleAddSectionOpen}
                >
                  Add New Section
                </Button>
              </Box>
            )}

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              Object.entries(groupedMaterials).length > 0 ? (
                Object.entries(groupedMaterials).map(([section, sectionMaterials]) => (
                  <Box key={section} sx={{ mb: 4 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'primary.main' }}>
                        {section}
                      </Typography>
                      {editMode && (
                        <IconButton 
                          color="error" 
                          onClick={() => deleteSection(section)}
                          size="small"
                        >
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </Box>
                    {renderEditableTable(section, sectionMaterials)}
                  </Box>
                ))
              ) : (
                <Box sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="body1" color="text.secondary">
                    No materials found. Click "Analyze Materials" to extract materials from project documents
                    {editMode && " or add materials manually"}.
                  </Typography>
                  {editMode && (
                    <Button
                      startIcon={<AddIcon />}
                      variant="outlined"
                      color="primary"
                      onClick={handleAddSectionOpen}
                      sx={{ mt: 2 }}
                    >
                      Add First Material
                    </Button>
                  )}
                </Box>
              )
            )}
            {materials.length > 0 && (
              <>
                {/* Summary header */}
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: 'primary.main' }}>
                  Section Subtotals Summary
                </Typography>
                {/* Subtotal summary for all sections as a table, styled like the other tables */}
                <TableContainer component={Paper} sx={{ mb: 2, border: '1px solid #e0e0e0', borderRadius: '2px' }}>
                  <Table size="small" sx={{ '& .MuiTableCell-root': { borderColor: '#e0e0e0', padding: '6px 8px' } }}>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                        <TableCell sx={{ fontWeight: 700 }}>Section</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>Subtotal</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {Object.entries(groupedMaterials).map(([section, sectionMaterials]) => (
                        <TableRow key={section} hover sx={{ '&:hover': { backgroundColor: '#fafafa' } }}>
                          <TableCell>{section}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>${sectionMaterials.reduce((sum, m) => sum + (m.net_price || 0), 0).toFixed(2)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                {/* Total cost */}
                <Box sx={{ mt: 1, textAlign: 'right' }}>
                  <Typography variant="subtitle2">Total Cost:</Typography>
                  <Typography variant="subtitle1" fontWeight="bold">
                    ${getTotalCost().toFixed(2)}
                  </Typography>
                </Box>
              </>
            )}
            {lastUpdated && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
                Last updated: {new Date(lastUpdated).toLocaleString()}
              </Typography>
            )}
          </CardContent>
        </Card>
      </Grid>

      {/* Sources */}
      <Grid item xs={12} lg={4}>
        <Card sx={{ borderRadius: '16px' }}>
          <CardHeader
            title="Material Sources"
            sx={{
              p: 3,
              pb: 0,
              '& .MuiCardHeader-title': {
                fontSize: '1.25rem',
                fontWeight: 600,
              },
            }}
          />
          <CardContent sx={{ p: 3 }}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : sources.length > 0 ? (
              <List dense>
                {sources.map((source, index) => (
                  <ListItem key={index} sx={{ px: 1 }}>
                    <ListItemIcon sx={{ minWidth: '36px' }}>
                      <AssignmentIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Link 
                          href={source} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                        >
                          <Typography variant="body2">
                            Source {index + 1}
                          </Typography>
                          <LaunchIcon fontSize="small" />
                        </Link>
                      }
                      secondary={null}
                    />
                  </ListItem>
                ))}
              </List>
            ) : (
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  No sources available. Analyze the project to retrieve material sources.
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      </Grid>
      {addSectionDialog}
      {materialEditPopover}
    </Grid>
  );
};

export default ProjectMaterials; 